import React from "react";
import { useParams } from "react-router-dom";
import Thumbnail from "react-webpage-thumbnail";

import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import StandardFileViewerPage from "./StandardFileViewerPage";

function parseLink(urlValue) {
    try {
        const url = new URL(urlValue);
        const hostname = url.hostname.replace(/^www\./, "").toLowerCase();
        let youTubeId = "";

        if (hostname === "youtu.be") {
            youTubeId = url.pathname.split("/").filter(Boolean)[0] || "";
        } else if (
            hostname === "youtube.com" ||
            hostname.endsWith(".youtube.com")
        ) {
            const pathParts = url.pathname.split("/").filter(Boolean);
            const embedIndex = pathParts.indexOf("embed");
            const shortsIndex = pathParts.indexOf("shorts");

            youTubeId =
                url.searchParams.get("v") ||
                (embedIndex >= 0 ? pathParts[embedIndex + 1] : "") ||
                (shortsIndex >= 0 ? pathParts[shortsIndex + 1] : "");
        }

        if (youTubeId) {
            return {
                kind: "youtube",
                hostname,
                embedUrl: `https://www.youtube.com/embed/${youTubeId}`,
            };
        }

        if (hostname === "docs.google.com") {
            return { kind: "google", hostname };
        }

        return { kind: "website", hostname };
    } catch (error) {
        return { kind: "website", hostname: urlValue };
    }
}

function LinkPreview({ file }) {
    const link = parseLink(file.filename);

    if (link.kind === "youtube") {
        return (
            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    pt: "56.25%",
                    backgroundColor: "#000",
                }}
            >
                <Box
                    component="iframe"
                    src={link.embedUrl}
                    title={file.displayName}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    sx={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        border: 0,
                    }}
                />
            </Box>
        );
    }

    if (link.kind === "google") {
        return (
            <Box
                sx={{
                    width: "100%",
                    height: {
                        xs: "min(62vh, 520px)",
                        sm: "min(68vh, 720px)",
                    },
                    minHeight: { xs: 360, sm: 480 },
                    backgroundColor: "background.media",
                    "& > div": {
                        width: "100% !important",
                        height: "100% !important",
                    },
                }}
            >
                <Thumbnail
                    url={file.filename}
                    iframeWidth={1440}
                    iframeHeight={900}
                    interactive
                    style={{ width: "100%", height: "100%" }}
                />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: { xs: 280, sm: 380 },
                px: 3,
                py: 5,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                background: (theme) =>
                    theme.palette.mode === "dark"
                        ? "radial-gradient(circle at 50% 38%, rgba(31, 184, 94, 0.15), transparent 40%), linear-gradient(145deg, #202923 0%, #151c18 100%)"
                        : "radial-gradient(circle at 50% 38%, rgba(0, 154, 68, 0.13), transparent 40%), linear-gradient(145deg, #f6faf7 0%, #e7f0e9 100%)",
            }}
        >
            <Box
                sx={{
                    width: { xs: 94, sm: 112 },
                    height: { xs: 94, sm: 112 },
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    color: "primary.main",
                    backgroundColor: "background.paper",
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow: "0 16px 40px rgba(20, 60, 35, 0.12)",
                }}
            >
                <LanguageOutlinedIcon
                    sx={{ fontSize: { xs: 46, sm: 56 } }}
                />
            </Box>
            <Typography
                variant="h6"
                sx={{ mt: 2.5, overflowWrap: "anywhere" }}
            >
                {link.hostname}
            </Typography>
            <Typography
                variant="body2"
                sx={{ mt: 0.75, color: "text.secondary", maxWidth: 520 }}
            >
                This website cannot be previewed here. Open it in a new tab to
                view the content.
            </Typography>
        </Box>
    );
}

export default function LinkViewerPage() {
    const { linkKey } = useParams();

    return (
        <StandardFileViewerPage
            fileKey={linkKey}
            routeSegment="link"
            mediaBackground="background.media"
            showDownloadAction={false}
            showPublicShareAction={false}
            deleteFromStorage={false}
            renderMedia={(file) => <LinkPreview file={file} />}
            renderAdditionalActions={(file) => (
                <Button
                    component="a"
                    href={file.filename}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outlined"
                    size="small"
                    startIcon={<OpenInNewIcon />}
                    sx={{
                        flex: {
                            xs: "1 1 calc(50% - 4px)",
                            sm: "0 0 auto",
                        },
                    }}
                >
                    Open link
                </Button>
            )}
        />
    );
}
